import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { hasAnalyticsConsent, subscribeCookieConsent } from '../lib/cookieConsent';

/**
 * GoogleAnalytics - GA4 integration for tracking page views and events
 * 
 * Setup:
 * 1. Create GA4 property at analytics.google.com
 * 2. Get Measurement ID (starts with G-)
 * 3. Add to .env: VITE_GA_MEASUREMENT_ID=G-XXXXXXXXXX
 * 
 * Features:
 * - Automatic page view tracking on route change
 * - E-commerce event helpers (view_item, add_to_cart, purchase)
 * - Only loads in production or when GA ID is set
 */

// Get GA ID from environment
const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;

// Declare gtag function type
declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

// Initialize gtag
const initGtag = () => {
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag(...args: unknown[]) {
    window.dataLayer.push(args);
  };
  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, {
    page_path: window.location.pathname,
  });
};

// Load GA script (called only after consent).
const loadGAScript = () => {
  if (!GA_MEASUREMENT_ID) return;

  if (document.querySelector(`script[src*="googletagmanager.com/gtag"]`)) {
    return;
  }

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  script.onload = initGtag;
};

const loadIfConsented = () => {
  if (hasAnalyticsConsent() && GA_MEASUREMENT_ID) {
    loadGAScript();
  }
};

/**
 * GoogleAnalytics Component
 * Add this once in your app (e.g., in Layout or App)
 */
export function GoogleAnalytics() {
  const location = useLocation();

  // Load GA only after visitor accepts analytics cookies (PIPEDA).
  useEffect(() => {
    loadIfConsented();
    return subscribeCookieConsent(() => loadIfConsented());
  }, []);

  useEffect(() => {
    if (GA_MEASUREMENT_ID && hasAnalyticsConsent() && window.gtag) {
      window.gtag('config', GA_MEASUREMENT_ID, {
        page_path: location.pathname + location.search,
        page_title: document.title,
      });
    }
  }, [location]);

  // Don't render anything
  return null;
}

/**
 * Track custom events
 * 
 * @example
 * trackEvent('add_to_cart', { item_name: 'Chocolate Cake', value: 45.00 })
 */
export function trackEvent(
  eventName: string,
  eventParams?: Record<string, unknown>
) {
  if (GA_MEASUREMENT_ID && hasAnalyticsConsent() && window.gtag) {
    window.gtag('event', eventName, eventParams);
  }
}

/**
 * E-commerce: Track product view
 */
export function trackViewItem(item: {
  id: string;
  name: string;
  category?: string;
  price?: number;
}) {
  trackEvent('view_item', {
    currency: 'CAD',
    value: item.price || 0,
    items: [{
      item_id: item.id,
      item_name: item.name,
      item_category: item.category || 'Bakery',
      price: item.price || 0,
    }],
  });
}

/**
 * E-commerce: Track add to cart
 */
export function trackAddToCart(item: {
  id: string;
  name: string;
  category?: string;
  price: number;
  quantity?: number;
}) {
  trackEvent('add_to_cart', {
    currency: 'CAD',
    value: item.price * (item.quantity || 1),
    items: [{
      item_id: item.id,
      item_name: item.name,
      item_category: item.category || 'Bakery',
      price: item.price,
      quantity: item.quantity || 1,
    }],
  });
}

/**
 * E-commerce: Track purchase/order
 */
export function trackPurchase(order: {
  transaction_id: string;
  value: number;
  items: Array<{
    id: string;
    name: string;
    price: number;
    quantity: number;
  }>;
}) {
  trackEvent('purchase', {
    transaction_id: order.transaction_id,
    currency: 'CAD',
    value: order.value,
    items: order.items.map(item => ({
      item_id: item.id,
      item_name: item.name,
      price: item.price,
      quantity: item.quantity,
    })),
  });
}

/**
 * Track contact/lead events
 */
export function trackContact(method: 'phone' | 'whatsapp' | 'messenger' | 'email' | 'form') {
  trackEvent('contact', {
    method,
    event_category: 'engagement',
  });
}

/**
 * Track share events
 */
export function trackShare(contentType: string, itemId: string, method: string) {
  trackEvent('share', {
    content_type: contentType,
    item_id: itemId,
    method,
  });
}

export default GoogleAnalytics;

